import { useEffect, useMemo, useState } from "react"
import { toast } from "react-toastify"
import { Dayjs } from "dayjs"

import { useLazyGetDevicesMessagesQuery, useScheduleMessageForDevicesMutation } from "store/api/contractorsApi"
import { useLazyGetTableColumnsQuery } from "store/api/dashboardApi"
import { useGetDevicesQuery } from "store/api/devicesApi"
import { useAppDispatch, useAppSelector } from "store/hooks"
import { showPerformanceTestConfirmModal } from "store/modalSlise"
import { setModalContent } from "store/tempDataSlise"
import { ApiTypes, UITypes } from "types"
import SubscriptionPrivilege from "types/enums/SubscriptionPrivilege"
import { ReactComponent as IconDelete } from "assets/svg/icon-delete.svg"

import { convertDevicesAndScheduledMessagesToTableRows } from "utils/convertData"
import getErrorMessage from "utils/getErrorMessage"
import hasSubscriptionAccess from "utils/hasSubscriptionAccess"
import { reorderTableHeaderCells } from "utils/reorderTabbleColumns"
import useUrlQueryParams from "hooks/useUrlQueryParams"
import { RemoveScheduledMessageModal } from "modals/RemoveScheduledMessageModal"
import { SubscriptionUpgradeModal } from "modals/SubscriptionUpgradeModal/SubscriptionUpgradeModal"
import { ContentWrapper } from "components/common/ContentWrapper"
import { Pagination } from "components/common/Pagination"
import TableWrapper from "components/table/TableWrapper"

import { dummyDevices, dummyMeta } from "../MonitoringPage/dummyMonitoringData"
import { MessageCenterHeader } from "./MessageCenterHeader"
import { messageCenterHeaderCells } from "./MessageCenterHeaderCells"

import styles from "./index.module.scss"

interface MessageCenterProps {
  hideUnavailableModal?: boolean
}

export const MessageCenter = ({ hideUnavailableModal = false }: MessageCenterProps) => {
  const { urlQueryParams } = useUrlQueryParams()
  const [showRemoveScheduledMessageModal, setShowRemoveScheduledMessageModal] = useState(false)
  const [toBeRemovedScheduledMessage, setToBeRemovedScheduledMessage] = useState<ApiTypes.Model.DeviceMessage | null>(
    null,
  )
  const [triggerGetDevicesMessages, { data: devicesMessagesData }] = useLazyGetDevicesMessagesQuery()
  const { data, isFetching } = useGetDevicesQuery(
    { ...urlQueryParams, phisical_status: "installed" },
    {
      pollingInterval: 30000,
      skipPollingIfUnfocused: true,
    },
  )
  const dispatch = useAppDispatch()

  useEffect(() => {
    if (data?.data?.length && hasMessagesCenterAccess) {
      triggerGetDevicesMessages(data?.data?.map(item => item?.id))
    }
  }, [data])

  const [triggerScheduleMessageForDevices, { isLoading: isScheduleMessageForDevicesLoading }] =
    useScheduleMessageForDevicesMutation()
  const [triggerGetTableColumns, { data: columnsData, isFetching: isGetTableColumnsFetching }] =
    useLazyGetTableColumnsQuery()
  const userProfile = useAppSelector(state => state.user.userProfile)
  const [templateID, setTemplateID] = useState<undefined | UITypes.Option[]>(undefined)
  const [selectedDevices, setSelectedDevices] = useState<ApiTypes.Model.Device[]>([])

  const isHeaderChecked = useMemo(() => {
    const currentPageDevices = data?.data || []
    if (!currentPageDevices.length) return false

    return currentPageDevices.every(device => selectedDevices.some(d => d.id === device.id))
  }, [data?.data, selectedDevices])
  const [isFirstRender, setFirstRender] = useState(true)
  const [tableData, setTableData] = useState<UITypes.TableBodyRow[]>([])
  const hasMessagesCenterAccess = userProfile
    ? hasSubscriptionAccess(userProfile, SubscriptionPrivilege.MessageCenter)
    : false

  const tableHeaderCells = useMemo(
    (): UITypes.TableHeaderCell[] =>
      columnsData?.data?.config
        ? reorderTableHeaderCells(messageCenterHeaderCells, columnsData?.data?.config)
        : messageCenterHeaderCells,
    [columnsData],
  )

  const toggleAllDevices = () => {
    const currentPageDevices = data?.data || []
    if (isHeaderChecked) {
      const currentPageDeviceIds = currentPageDevices.map(d => d.id)
      setSelectedDevices(prev => prev.filter(d => !currentPageDeviceIds.includes(d.id)))
    } else {
      setSelectedDevices(prev => {
        const existingIds = new Set(prev.map(d => d.id))
        const toAdd = currentPageDevices.filter(d => !existingIds.has(d.id))
        return [...prev, ...toAdd]
      })
    }
  }

  const onSelectDevice = (id?: number) => {
    const devices = data?.data || []
    if (selectedDevices.some(d => d.id === id)) {
      setSelectedDevices(prev => prev.filter(d => d.id !== id))
    } else {
      const device = devices.find(d => d.id === id)
      if (device) setSelectedDevices(prev => [...prev, device])
    }
  }

  const handleSendMessage = async () => {
    if (!templateID) return
    const data = {
      device_ids: selectedDevices.map(d => d.id),
      message_template_id: templateID[0]?.value as number,
      scheduled_delivery_timestamp: "immediately",
    }
    try {
      const res = await triggerScheduleMessageForDevices(data)
      const error = getErrorMessage(res?.error)
      if (error) {
        toast.error(error)
      } else {
        toast.success(
          "The messages will be sent to the selected group during 1 hour. Navigate to History  page to check the status of the sent messages.",
        )
        setSelectedDevices([])
        setTemplateID(undefined)
      }
    } catch (error) {
      console.error(error)
    }
  }

  const handleSendScheduledMessage = async (selectedDateTime: Dayjs) => {
    if (!templateID) return

    const data = {
      device_ids: selectedDevices.map(d => d.id),
      scheduled_delivery_timestamp: selectedDateTime.toISOString(),
      message_template_id: templateID[0]?.value as number,
    }

    try {
      const res = await triggerScheduleMessageForDevices(data)
      const error = getErrorMessage(res?.error)

      if (error) {
        toast.error(error)
      } else {
        setSelectedDevices([])
        setTemplateID(undefined)

        dispatch(
          setModalContent({
            title: "Successfully scheduled",
            content: (
              <>
                Message will be sent to the customer(s) on <br /> {selectedDateTime.format("MMMM DD, YYYY hh:mm A")}
              </>
            ),
          }),
        )
        dispatch(showPerformanceTestConfirmModal(true))
      }
    } catch (error) {
      console.error(error)
    }
  }

  const meta = hasMessagesCenterAccess ? data?.meta : dummyMeta

  const devices = useMemo(() => {
    if (!hasMessagesCenterAccess) {
      return dummyDevices
    }

    return data?.data || []
  }, [data, hasMessagesCenterAccess])

  useEffect(() => {
    if (isFirstRender) {
      setFirstRender(false)
      return
    }
    triggerGetTableColumns({ list_name: "contractor_messages_list" })
  }, [isFirstRender])

  const onRemoveScheduledMessageClick = (id: number) => {
    setShowRemoveScheduledMessageModal(true)

    const deviceMessage = devicesMessagesData?.data
      ?.flatMap(item => item?.messages?.find(item => item?.message_id === id))
      .filter(item => item !== undefined)

    if (deviceMessage) {
      setToBeRemovedScheduledMessage(deviceMessage[0])
    }
  }

  const getRowActions = (rowData: UITypes.TableBodyRow) => ({
    className: styles.messageCenterRemoveButtonContainer,
    actionButtons: [
      {
        icon: <IconDelete />,
        tooltip: "Cancel scheduled message",
        tooltipPosition: "top-start" as const,
        hidden: !rowData.scheduled_message.value,
        handler: () => onRemoveScheduledMessageClick(rowData.rowSettings.item?.message_id),
      },
    ],
  })

  useEffect(() => {
    if (
      (devices?.length &&
        devicesMessagesData?.data?.length &&
        devicesMessagesData?.data?.find(item => item?.device_id === devices[0]?.id)) ||
      devices?.length === 0
    ) {
      setTableData(convertDevicesAndScheduledMessagesToTableRows(devices, devicesMessagesData?.data || []))
    }
  }, [devices, devicesMessagesData])

  return (
    <>
      <ContentWrapper title="Message Center" className={styles.messageCenterContentWrapper}>
        <MessageCenterHeader
          templateID={templateID}
          setTemplateID={setTemplateID}
          deviceIDs={selectedDevices.map(d => d.id)}
          onSendMessage={handleSendMessage}
          onSendScheduledMessage={handleSendScheduledMessage}
          isGenerateMessagesFetching={isScheduleMessageForDevicesLoading}
          tableHeaderCells={tableHeaderCells}
          hasMessagesCenterAccess={hasMessagesCenterAccess}
        />
        <TableWrapper
          headerCells={tableHeaderCells}
          columnsLoaded={!!columnsData}
          data={tableData}
          isLoading={isFetching || isGetTableColumnsFetching}
          getRowActions={getRowActions}
          onCheckboxChange={onSelectDevice}
          onHeaderCheckboxChange={toggleAllDevices}
          currentItemIDs={selectedDevices.map(d => d.id)}
          isHeaderChecked={isHeaderChecked}
          rowsExpanded
          additionalTags={
            selectedDevices?.length
              ? [
                  {
                    label: `Selected Device${selectedDevices.length > 1 ? "s" : ""} S/N`,
                    values: selectedDevices?.map(item => item.sn),
                  },
                ]
              : []
          }
          clearAdditionalTags={() => setSelectedDevices([])}
        />
        <Pagination meta={meta} />
      </ContentWrapper>
      {!hasMessagesCenterAccess && (
        <SubscriptionUpgradeModal
          page={SubscriptionPrivilege.MessageCenter}
          hideUnavailableModal={hideUnavailableModal}
        />
      )}
      {showRemoveScheduledMessageModal && toBeRemovedScheduledMessage != null && (
        <RemoveScheduledMessageModal
          deviceMessage={toBeRemovedScheduledMessage}
          onCancel={() => {
            setShowRemoveScheduledMessageModal(false)
            setToBeRemovedScheduledMessage(null)
          }}
          onSuccess={() => {
            setShowRemoveScheduledMessageModal(false)
            setToBeRemovedScheduledMessage(null)
          }}
        />
      )}
    </>
  )
}
